import { uploadBytes } from "firebase/storage";

// Full-bleed heroes are the widest thing we render; 2400px covers them on
// retina laptops without shipping camera-sized originals.
const MAX_DIMENSION = 2400;
const WEBP_QUALITY = 0.82;

// Every upload path is timestamped, so a URL's bytes never change — safe for
// browsers/CDN to cache for a year instead of Storage's default max-age=0.
const CACHE_CONTROL = "public, max-age=31536000, immutable";

const SKIP_TYPES = ["image/gif", "image/svg+xml"];

// Some OS/browser combos hand .avif files over with an empty MIME type, so
// fall back to the extension for those.
const EXTENSION_TYPES = { avif: "image/avif" };

/** The file's image MIME type, inferred from its extension when the browser
 * leaves it blank. */
export function imageMimeType(file) {
  if (file?.type) return file.type;
  const ext = file?.name?.split(".").pop()?.toLowerCase();
  return EXTENSION_TYPES[ext] || "";
}

export const isImageFile = (file) => imageMimeType(file).startsWith("image/");

// File-input accept list: image/* plus explicit .avif for pickers that don't
// map it to image/*.
export const IMAGE_ACCEPT = "image/*,.avif";

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/** Downscales to MAX_DIMENSION and re-encodes as WebP. Returns the original
 * file when it can't be decoded, is animated/vector, or the result isn't
 * actually smaller. */
async function compressImage(file) {
  if (!isImageFile(file) || SKIP_TYPES.includes(imageMimeType(file))) {
    return file;
  }
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/webp", WEBP_QUALITY),
    );
    if (!blob || blob.type !== "image/webp" || blob.size >= file.size) {
      return file;
    }
    return blob;
  } catch {
    return file;
  }
}

/** Compresses an image and uploads it with long-lived cache headers. Use for
 * public site imagery (CMS, catalog listings); returns the upload snapshot
 * just like uploadBytes. */
export async function uploadOptimizedImage(storageRef, file) {
  const data = await compressImage(file);
  return uploadBytes(storageRef, data, {
    contentType: data.type || imageMimeType(file),
    cacheControl: CACHE_CONTROL,
  });
}
